import React, { useEffect, useLayoutEffect, createContext, useState, useCallback, useRef } from 'react';
import AuthService from '../../api/services/AuthService';
import { alertErrorMessage } from '../../customComponents/CustomAlertMessage';
import { DISABLE_ALL_NETWORK } from '../../networkConfig';
import { extractWalletTypesArrayFromResponse, normalizeWalletTypesList } from '../../utils/walletTypesNormalize';
import { normalizeEstimatedPortfolioData } from '../../utils/normalizeEstimatedPortfolio';

const DEMO_USER_DETAILS = {
  emailId: 'demo@example.com',
  uuid: 'DEMO-USER-001',
  last_login_at: new Date().toISOString(),
  last_login_ip: '127.0.0.1',
  createdAt: '2024-01-01T00:00:00.000Z',
  referral_code: 'DEMO',
  kycVerified: 2,
  '2fa': 1,
  profilepicture: null,
};

/**
 * Full estimated-portfolio (`walletType=""`) includes `wallet_types`. Filtered calls (e.g. `spot`)
 * often omit it but still replace global state — keep the last known breakdown for Account tab UIs.
 */
function mergeEstimatedPortfolioState(prev, incoming) {
  if (!incoming || typeof incoming !== "object") {
    return prev && typeof prev === "object" ? prev : {};
  }
  const out = { ...incoming };
  const incomingWt = out.wallet_types ?? out.walletTypes;
  const hasIncomingWt = Array.isArray(incomingWt) && incomingWt.length > 0;
  if (hasIncomingWt) {
    out.wallet_types = incomingWt;
  } else if (Array.isArray(prev?.wallet_types) && prev.wallet_types.length > 0) {
    out.wallet_types = prev.wallet_types;
  }
  return out;
}

export const ProfileContext = createContext();

function truthySecurityFlag(v) {
  if (v === true || v === 1) return true;
  if (v == null || v === false || v === 0) return false;
  const s = String(v).toLowerCase();
  return s === "true" || s === "1" || s === "yes";
}

function pickSecurityMethodsPayload(result) {
  if (!result || typeof result !== "object") return null;
  const d = result.data;
  if (d?.security_methods && typeof d.security_methods === "object") return d.security_methods;
  if (d?.data?.security_methods && typeof d.data.security_methods === "object") return d.data.security_methods;
  if (result.security_methods && typeof result.security_methods === "object") return result.security_methods;
  if (d?.securityMethods && typeof d.securityMethods === "object") return d.securityMethods;
  if (d && typeof d === "object" && !Array.isArray(d)) {
    const looksLikeFlags =
      "totp" in d ||
      "email" in d ||
      "mobile" in d ||
      "phone" in d ||
      "sms" in d ||
      "googleAuth" in d ||
      "google_auth" in d ||
      "passkey" in d ||
      "webauthn" in d;
    if (looksLikeFlags) return d;
  }
  return null;
}

function normalizeSecurityMethodsFlags(m) {
  if (!m || typeof m !== "object") {
    return { passkey: false, totp: false, email: false, mobile: false, phone: false };
  }
  const anyTrue = (...vals) => vals.some((v) => truthySecurityFlag(v));
  const mobile = anyTrue(m.mobile, m.phone, m.sms, m.Mobile, m.Phone, m.SMS);
  return {
    passkey: anyTrue(m.passkey, m.webauthn, m.WebAuthn),
    totp: anyTrue(
      m.totp,
      m.TOTP,
      m.google_auth,
      m.googleAuth,
      m.authenticator,
      m.google_authenticator,
      m.googleAuthenticator
    ),
    email: anyTrue(m.email, m.Email),
    mobile,
    phone: mobile,
  };
}

export function ProfileProvider(props) {
  const [profileHydrated, setProfileHydrated] = useState(false);
  const [userDetails, setUserDetails] = useState({});
  const [loginDetails, setLoginDetails] = useState([]);
  const [newStoredTheme, setNewStoredTheme] = useState('');
  const [lastLogin, setLastLogin] = useState("");
  const [currentPage, setCurrentPage] = useState("Dashboard");
  const [themeUpdated] = useState(true);
  const setThemeUpdated = useCallback(() => { }, []);
  const [refreshModal, setRefreshModal] = useState(true);
  const [refreshNotification, setRefreshNotification] = useState(false);
  const [activeTab, setActiveTab] = useState("");

  // Shared state for APIs that should only run once after login
  const [estimatedPortfolio, setEstimatedPortfolio] = useState({});
  const [notifications, setNotifications] = useState([]);
  const [notificationCounts, setNotificationCounts] = useState({ unseen: 0 });
  const [pairs, setPairs] = useState([]);
  const [walletTypes, setWalletTypes] = useState([]);
  const [securityMethods, setSecurityMethods] = useState(null);

  // Refs to track if APIs have been called
  const isInitialized = useRef(false);
  const estPortfolioCacheRef = useRef(new Map()); // key: walletType -> { data, ts }
  const estPortfolioInFlightRef = useRef(new Map()); // key: walletType -> Promise
  const securityMethodsCacheRef = useRef({ data: null, ts: 0 });
  const securityMethodsInFlightRef = useRef(null); // Promise | null

  const [token, setToken] = useState(() => localStorage.getItem("token"));

  const syncTokenFromStorage = useCallback(() => {
    const t = localStorage.getItem("token");
    setToken(t);
  }, []);

  useEffect(() => {
    // Keep auth state in sync without requiring full page reloads.
    const onStorage = (e) => {
      if (!e || e.key == null || e.key === "token") {
        syncTokenFromStorage();
      }
    };
    const onAuthChanged = () => syncTokenFromStorage();
    window.addEventListener("storage", onStorage);
    window.addEventListener("agce-auth-changed", onAuthChanged);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("agce-auth-changed", onAuthChanged);
    };
  }, [syncTokenFromStorage]);

  const updateModelHideStatus = (value) => {
    localStorage.setItem(value, true);
    setRefreshModal(!refreshModal);
  };

  const handleUserDetails = useCallback(async () => {
    try {
      if (DISABLE_ALL_NETWORK) {
        setUserDetails(DEMO_USER_DETAILS);
        return;
      }
      try {
        const result = await AuthService.getDetails();
        if (result?.success) {
          const data = result?.data || {};
          const profilePic =
            data.profilePicture ??
            data.profilepicture ??
            data.profile_picture ??
            data.avatar ??
            null;
          setUserDetails({
            ...data,
            ...(profilePic
              ? { profilePicture: profilePic, profilepicture: profilePic }
              : {}),
          });
        } else if (result?.message) {
          alertErrorMessage(result?.message);
        }
      } catch (error) {
        // Silent fail
      }
    } finally {
      setProfileHydrated(true);
    }
  }, []);

  const activityLogs = useCallback(async () => {
    if (DISABLE_ALL_NETWORK) {
      setLastLogin(DEMO_USER_DETAILS.last_login_at);
      return;
    }
    try {
      const result = await AuthService.getActivityLogs(0, 1);
      if (result?.success) {
        setLastLogin(result?.data[0]?.createdAt);
      }
    } catch {
      // Silent fail
    }
  }, []);

  // Fetch estimated portfolio - shared across components (cached + in-flight de-dupe)
  const fetchEstimatedPortfolio = useCallback(async (type = "", opts = {}) => {
    const walletType = String(type ?? "").trim().toLowerCase();
    const force = opts?.force === true;
    const cacheTtlMs = typeof opts?.cacheTtlMs === "number" ? opts.cacheTtlMs : 60_000; // 60s

    if (DISABLE_ALL_NETWORK) {
      const demo = {
        dollarPrice: 12456.78,
        estimated_total_usdt: 12456.78,
        estimated_total_preferred: 1023456,
        currency_prefrence: "INR",
        currencyPrice: 1023456,
        Currency: "INR",
      };
      setEstimatedPortfolio(demo);
      estPortfolioCacheRef.current.set(walletType, { data: demo, ts: Date.now() });
      return demo;
    }
    const cached = estPortfolioCacheRef.current.get(walletType);
    if (!force && cached?.data && Date.now() - (cached.ts || 0) < cacheTtlMs) {
      let mergedReturn = null;
      setEstimatedPortfolio((prev) => {
        mergedReturn = mergeEstimatedPortfolioState(prev, cached.data);
        return mergedReturn;
      });
      return mergedReturn;
    }

    const inflight = estPortfolioInFlightRef.current.get(walletType);
    if (!force && inflight) return inflight;

    const p = (async () => {
      try {
        const result = await AuthService.estimatedPortfolio(walletType);
        if (result?.success) {
          const raw = result?.data || {};
          const data = normalizeEstimatedPortfolioData(raw, walletType);
          let mergedResult = null;
          setEstimatedPortfolio((prev) => {
            mergedResult = mergeEstimatedPortfolioState(prev, data);
            return mergedResult;
          });
          estPortfolioCacheRef.current.set(walletType, { data, ts: Date.now() });
          return mergedResult;
        }
        estPortfolioCacheRef.current.delete(walletType);
      } catch {
        estPortfolioCacheRef.current.delete(walletType);
        // Silent fail
      }
      return null;
    })();

    estPortfolioInFlightRef.current.set(walletType, p);
    try {
      return await p;
    } finally {
      estPortfolioInFlightRef.current.delete(walletType);
    }
  }, []);

  // Refresh portfolio (for manual refresh from components)
  const refreshEstimatedPortfolio = useCallback(async (type = "") => {
    return await fetchEstimatedPortfolio(type, { force: true, cacheTtlMs: 0 });
  }, [fetchEstimatedPortfolio]);

  // Fetch notifications - shared across components
  const fetchNotifications = useCallback(async () => {
    if (DISABLE_ALL_NETWORK) {
      setNotifications([]);
      setNotificationCounts({ unseen: 0 });
      return null;
    }
    try {
      const result = await AuthService.notifications({ page: 1, limit: 20 });
      if (result?.success) {
        const raw = Array.isArray(result?.data) ? result.data : [];
        setNotifications(raw.length ? [...raw].reverse() : []);
        setNotificationCounts(result?.counts || { unseen: 0 });
        return result;
      }
    } catch {
      // Silent fail
    }
    return null;
  }, []);

  // Fetch pairs - shared across components
  const fetchPairs = useCallback(async () => {
    if (DISABLE_ALL_NETWORK) {
      setPairs([]);
      return [];
    }
    try {
      const result = await AuthService.getPairs();
      if (result?.success) {
        setPairs(result?.data || []);
        return result?.data;
      }
    } catch {
      // Silent fail
    }
    return null;
  }, []);

  // Fetch wallet types - shared across components
  const fetchWalletTypes = useCallback(async () => {
    if (DISABLE_ALL_NETWORK) {
      setWalletTypes([]);
      return [];
    }
    try {
      const result = await AuthService.availableWalletTypes();
      if (result?.success) {
        const raw = extractWalletTypesArrayFromResponse(result?.data);
        const norm = normalizeWalletTypesList(raw);
        setWalletTypes(norm);
        return norm;
      }
    } catch {
      // Silent fail
    }
    return null;
  }, []);

  // Fetch security methods - shared across components (cached + in-flight de-dupe)
  const fetchSecurityMethods = useCallback(async (opts = {}) => {
    const force = opts?.force === true;
    const cacheTtlMs = typeof opts?.cacheTtlMs === "number" ? opts.cacheTtlMs : 60_000; // 60s
    const now = Date.now();

    const cached = securityMethodsCacheRef.current;
    if (!force && cached?.data && now - (cached.ts || 0) < cacheTtlMs) {
      return cached.data;
    }

    if (!force && securityMethodsInFlightRef.current) {
      return securityMethodsInFlightRef.current;
    }

    const p = (async () => {
      try {
        const result = await AuthService.getSecurityMethodsList();
        const payload = pickSecurityMethodsPayload(result);
        if (!result?.success || !payload) return null;
        const norm = normalizeSecurityMethodsFlags(payload);
        securityMethodsCacheRef.current = { data: norm, ts: Date.now() };
        setSecurityMethods(norm);
        return norm;
      } catch {
        return null;
      } finally {
        securityMethodsInFlightRef.current = null;
      }
    })();

    securityMethodsInFlightRef.current = p;
    return p;
  }, []);

  const refreshSecurityMethods = useCallback(async () => {
    return await fetchSecurityMethods({ force: true });
  }, [fetchSecurityMethods]);

  // Generate socket IDs
  const generateSocketUniqueId = useCallback((length = 15) => {
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let uniqueId = '';
    for (let i = 0; i < length; i++) {
      const randomIndex = Math.floor(Math.random() * characters.length);
      uniqueId += characters.charAt(randomIndex);
    }
    localStorage.setItem("socketId", uniqueId);
  }, []);

  const generateFutureSocketUniqueId = useCallback((length = 15) => {
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let uniqueId = '';
    for (let i = 0; i < length; i++) {
      const randomIndex = Math.floor(Math.random() * characters.length);
      uniqueId += characters.charAt(randomIndex);
    }
    localStorage.setItem("socketIdFuture", uniqueId);
  }, []);

  // Initialize all APIs once after login
  useEffect(() => {
    // Always generate socket IDs
    generateSocketUniqueId();
    generateFutureSocketUniqueId();

    // Only fetch user-specific data if logged in and not already initialized
    if (token && !isInitialized.current) {
      isInitialized.current = true;

      // Run all initial API calls
      handleUserDetails();
      activityLogs();
      fetchEstimatedPortfolio("");
      fetchNotifications();
      fetchPairs();
      fetchWalletTypes();
    }

    // Reset initialization flag when token is cleared (logout)
    if (!token) {
      isInitialized.current = false;
      setProfileHydrated(false);
      setUserDetails({});
      setLoginDetails([]);
      setEstimatedPortfolio({});
      estPortfolioCacheRef.current.clear();
      estPortfolioInFlightRef.current.clear();
      setNotifications([]);
      setNotificationCounts({ unseen: 0 });
      setPairs([]);
      setWalletTypes([]);
      setSecurityMethods(null);
      securityMethodsCacheRef.current = { data: null, ts: 0 };
      securityMethodsInFlightRef.current = null;
    }
  }, [token, handleUserDetails, activityLogs, fetchEstimatedPortfolio, fetchNotifications, fetchPairs, fetchWalletTypes, generateSocketUniqueId, generateFutureSocketUniqueId]);

  useEffect(() => {
    const onKyc = () => {
      if (token) handleUserDetails();
    };
    window.addEventListener("agce-kyc-status", onKyc);
    return () => window.removeEventListener("agce-kyc-status", onKyc);
  }, [token, handleUserDetails]);

  // Refresh notifications when refreshNotification flag changes
  useEffect(() => {
    if (token && refreshNotification) {
      fetchNotifications();
    }
  }, [refreshNotification, token, fetchNotifications]);

  const handleTheme = () => {
    const htmlTag = document.documentElement;
    const currentTheme = htmlTag.getAttribute('data-theme');
    const newTheme = currentTheme === 'light' ? 'dark' : 'light';
    localStorage.setItem('app_theme', newTheme);
    htmlTag.setAttribute('data-theme', newTheme);

    if (newTheme === 'light') {
      document.body.classList.add("light_theme");
      document.body.classList.remove("dark_theme");
      document.body.setAttribute("data-theme", "light");
    } else {
      document.body.classList.remove("light_theme");
      document.body.classList.add("dark_theme");
      document.body.setAttribute("data-theme", "dark");
    }

    setNewStoredTheme(newTheme);
  };

  // Initialize theme on mount
  useLayoutEffect(() => {
    const htmlTag = document.documentElement;
    let storedTheme = localStorage.getItem("app_theme");
    if (!storedTheme) {
      storedTheme = "dark";
      localStorage.setItem("app_theme", "dark");
    }

    htmlTag.setAttribute("data-theme", storedTheme);

    if (storedTheme === 'light') {
      document.body.classList.add("light_theme");
      document.body.classList.remove("dark_theme");
      document.body.setAttribute("data-theme", "light");
    } else {
      document.body.classList.remove("light_theme");
      document.body.classList.add("dark_theme");
      document.body.setAttribute("data-theme", "dark");
    }

    setNewStoredTheme(storedTheme);
  }, []);

  return (
    <ProfileContext.Provider value={{
      token,
      setToken,
      /** Becomes true after the first post-login `handleUserDetails` attempt finishes (success or failure). */
      profileHydrated,
      // Existing values
      refreshNotification,
      setRefreshNotification,
      updateModelHideStatus,
      currentPage,
      setCurrentPage,
      lastLogin,
      userDetails,
      setUserDetails,
      handleUserDetails,
      setLoginDetails,
      loginDetails,
      handleTheme,
      newStoredTheme,
      activeTab,
      setActiveTab,
      themeUpdated,
      setThemeUpdated,

      // New shared state
      estimatedPortfolio,
      fetchEstimatedPortfolio,
      refreshEstimatedPortfolio,
      notifications,
      notificationCounts,
      fetchNotifications,
      pairs,
      fetchPairs,
      walletTypes,
      fetchWalletTypes,
      securityMethods,
      fetchSecurityMethods,
      refreshSecurityMethods,
    }}>
      {props.children}
    </ProfileContext.Provider>
  );
}
